import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db } from '@siaedts/shared/lib/supabase';
import { localInquiries, localMessages, queueSyncOperation } from '@siaedts/shared';
import { useTranslation } from 'react-i18next';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
  preferredContact: string;
  newsletter: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
}

const subjects = [
  'General Inquiry',
  'Product Information',
  'Pricing & Quotes',
  'Technical Support',
  'Parts & Service',
  'Financing Options',
  'Career Opportunities',
  'Partnership Inquiry',
  'Media & Press',
  'Other',
];

const INQUIRY_SUBJECTS = new Set([
  'General Inquiry',
  'Product Information',
  'Pricing & Quotes',
  'Financing Options',
  'Parts & Service',
  'Partnership Inquiry',
]);

const countryCodes = [
  { code: '+224', country: 'Guinea' },
  { code: '+33', country: 'France' },
  { code: '+1', country: 'United States / Canada' },
  { code: '+44', country: 'United Kingdom' },
  { code: '+221', country: 'Senegal' },
  { code: '+225', country: 'Côte d’Ivoire' },
  { code: '+212', country: 'Morocco' },
  { code: '+216', country: 'Tunisia' },
  { code: '+234', country: 'Nigeria' },
  { code: '+27', country: 'South Africa' },
];

const contactInfo = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Visit Our Showroom',
    details: [
      '123 Agricultural Way',
      'Farming District, FD 12345',
      'Open Mon-Fri: 8AM - 6PM',
      'Sat: 9AM - 4PM',
    ],
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    title: 'Call Us',
    details: [
      '+224 622 702 486',
    ],
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Email Us',
    details: [
      'siaedtssarl@gmail.com',
    ],
  },
];

export function Contact() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    preferredContact: 'email',
    newsletter: false,
  });
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+224');

  useEffect(() => { void db.about.get().then((content) => { if (content?.whatsapp_number) setWhatsappNumber(content.whatsapp_number); }).catch(() => undefined); }, []);

  useEffect(() => {
    const urlSubject = searchParams.get('subject');
    const urlProductId = searchParams.get('productId');
    const urlProductName = searchParams.get('productName');

    if (urlSubject || urlProductName) {
      setFormData(prev => ({
        ...prev,
        subject: urlSubject && subjects.includes(urlSubject) ? urlSubject : prev.subject || 'Pricing & Quotes',
        message: urlProductName
          ? `Hello, I would like to request information and pricing for "${urlProductName}". Please contact me with availability and details.`
          : prev.message,
      }));
    }
    if (urlProductId) {
      setSelectedProductId(urlProductId);
    }
  }, [searchParams]);
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('contact.errors.firstNameRequired');
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('contact.errors.lastNameRequired');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('contact.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.errors.emailInvalid');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('contact.phone');
    }
    
    if (!formData.subject) {
      newErrors.subject = t('contact.errors.subjectRequired');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t('contact.errors.messageRequired');
    } else if (formData.message.trim().length < 20) {
      newErrors.message = t('contact.errors.messageLength');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const isCommercialInquiry = INQUIRY_SUBJECTS.has(formData.subject) || Boolean(selectedProductId);
    const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
    const fullPhoneNumber = `${phoneCountryCode}${formData.phone.trim().replace(/^0+/, '')}`;
    const formattedMessage = formData.company.trim()
      ? `${formData.message.trim()}\n\nCompany/Farm: ${formData.company.trim()}\nPreferred contact: ${formData.preferredContact}`
      : `${formData.message.trim()}\n\nPreferred contact: ${formData.preferredContact}`;
    const recordId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
    
    try {
      if (isCommercialInquiry) {
        const inquiryPayload = {
          id: recordId,
          customer_name: fullName,
          email: formData.email.trim(),
          phone: fullPhoneNumber,
          subject: formData.subject,
          message: formattedMessage,
          product_id: selectedProductId || null,
          status: 'new' as const,
        };

        await db.inquiries.create(inquiryPayload as never);
        await localInquiries.create(inquiryPayload);
      } else {
        const messagePayload = {
          id: recordId,
          name: fullName,
          email: formData.email.trim(),
          phone: fullPhoneNumber,
          subject: formData.subject,
          status: 'unread' as const,
          message: formattedMessage,
        };

        await db.contactMessages.create(messagePayload as never);
        await localMessages.create(messagePayload);
      }
    } catch (err) {
      console.warn('Could not submit directly to database, queued locally:', err);
      if (isCommercialInquiry) {
        const localInquiry = await localInquiries.create({
          customer_name: fullName,
          email: formData.email.trim(),
          phone: fullPhoneNumber,
          subject: formData.subject,
          message: formattedMessage,
          product_id: selectedProductId || null,
        });
        await queueSyncOperation({
          entity: 'inquiries',
          entity_id: localInquiry.id,
          operation: 'create',
          payload: localInquiry,
        });
      } else {
        const localMessage = await localMessages.create({
          name: fullName,
          email: formData.email.trim(),
          phone: fullPhoneNumber,
          subject: formData.subject,
          message: formattedMessage,
        });
        await queueSyncOperation({
          entity: 'messages',
          entity_id: localMessage.id,
          operation: 'create',
          payload: localMessage,
        });
      }
    }
    
    setIsSubmitting(false);
    setSubmitStatus('success');
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      subject: '',
      message: '',
      preferredContact: 'email',
      newsletter: false,
    });
    setSelectedProductId(null);
    setPhoneCountryCode('+224');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name, value, type } = target;
    const checked = target.type === 'checkbox' ? (target as HTMLInputElement).checked : undefined;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <>
      {/* Page Header */}
      <section className="bg-green-700 text-white py-16 sm:py-24">
        <div className="container-main">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">{t('contact.title')}</h1>
            <p className="text-green-100 text-lg">
              {t('contact.description')}
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              {contactInfo.map((info, index) => (
                <div key={index} className="card p-6">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center text-green-700 mb-4">
                    {info.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{info.title}</h3>
                  <ul className="space-y-2 text-gray-600">
                    {info.details.map((detail, i) => (
                      <li key={i} className="text-sm">{info.title === 'Call Us' ? <a href="tel:+224622702486" className="hover:text-green-700">{detail}</a> : info.title === 'Email Us' ? <a href="mailto:siaedtssarl@gmail.com" className="hover:text-green-700">{detail}</a> : detail}</li>
                    ))}
                  </ul>
                </div>
              ))}

              {whatsappNumber && <a href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="card flex items-center gap-4 p-6 border-green-200 hover:border-green-400 transition-colors"><div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center text-green-700 font-bold">WA</div><div><h3 className="text-lg font-semibold text-gray-900">WhatsApp</h3><p className="text-sm text-gray-600">Chat with our team directly</p><p className="text-sm font-medium text-green-700">{whatsappNumber}</p></div></a>}

              {/* Social Links */}
              <div className="card p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('contact.followUs')}</h3>
                <div className="flex space-x-4">
                  {[
                    { href: '#', label: 'Facebook', icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg> },
                    { href: '#', label: 'Instagram', icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg> },
                    { href: '#', label: 'LinkedIn', icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg> },
                    { href: '#', label: 'YouTube', icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg> },
                  ].map((social) => (
                    <a key={social.label} href={social.href} className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-gray-600 hover:bg-green-100 hover:text-green-700 transition-colors" aria-label={social.label}>
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="card p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.sendMessage')}</h2>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="alert">
                    <div className="flex items-center space-x-3">
                      <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <p className="font-medium text-green-800">{t('contact.successTitle')}</p>
                        <p className="text-sm text-green-700">{t('contact.successDesc')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                    <div className="flex items-center space-x-3">
                      <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <div>
                        <p className="font-medium text-red-800">{t('contact.errorTitle')}</p>
                        <p className="text-sm text-red-700">{t('contact.errorDesc')}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="label">{t('contact.firstName')}</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`input ${errors.firstName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        aria-invalid={errors.firstName ? 'true' : 'false'}
                        aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                        disabled={isSubmitting}
                      />
                      {errors.firstName && (
                        <p id="firstName-error" className="mt-1 text-sm text-red-600" role="alert">{errors.firstName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="lastName" className="label">{t('contact.lastName')}</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`input ${errors.lastName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        aria-invalid={errors.lastName ? 'true' : 'false'}
                        aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                        disabled={isSubmitting}
                      />
                      {errors.lastName && (
                        <p id="lastName-error" className="mt-1 text-sm text-red-600" role="alert">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="label">{t('contact.email')}</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`input ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        aria-invalid={errors.email ? 'true' : 'false'}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                        disabled={isSubmitting}
                      />
                      {errors.email && (
                        <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">{errors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="label">{t('contact.phone')} *</label>
                      <div className="flex gap-2">
                        <select value={phoneCountryCode} onChange={(event) => setPhoneCountryCode(event.target.value)} className="input w-[112px] shrink-0" aria-label="Country code" disabled={isSubmitting}>
                          {countryCodes.map((country) => <option key={country.code} value={country.code}>{country.code}</option>)}
                        </select>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`input min-w-0 flex-1 ${errors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                          placeholder="622 702 486"
                          aria-invalid={errors.phone ? 'true' : 'false'}
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.phone && <p className="mt-1 text-sm text-red-600" role="alert">{errors.phone}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="label">{t('contact.company')}</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="input"
                      placeholder={t('contact.optional')}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="label">{t('contact.subject')}</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`input ${errors.subject ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                      aria-invalid={errors.subject ? 'true' : 'false'}
                      aria-describedby={errors.subject ? 'subject-error' : undefined}
                      disabled={isSubmitting}
                    >
                      <option value="">{t('contact.selectSubject')}</option>
                      {subjects.map((subject) => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                    {errors.subject && (
                      <p id="subject-error" className="mt-1 text-sm text-red-600" role="alert">{errors.subject}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="label">{t('contact.message')}</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className={`input resize-none ${errors.message ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                      aria-invalid={errors.message ? 'true' : 'false'}
                      aria-describedby={errors.message ? 'message-error' : 'message-hint'}
                      placeholder={t('contact.messagePlaceholder')}
                      disabled={isSubmitting}
                    />
                    {errors.message ? (
                      <p id="message-error" className="mt-1 text-sm text-red-600" role="alert">{errors.message}</p>
                    ) : (
                      <p id="message-hint" className="mt-1 text-sm text-gray-500">{t('contact.minimumChars')}</p>
                    )}
                  </div>

                  <fieldset className="space-y-4">
                    <legend className="label">{t('contact.preferredContact')}</legend>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {[
                        { value: 'email', label: 'Email', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> },
                        { value: 'phone', label: 'Phone', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg> },
                        { value: 'both', label: 'Either', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg> },
                      ].map((option) => (
                        <label key={option.value} className="relative cursor-pointer">
                          <input
                            type="radio"
                            name="preferredContact"
                            value={option.value}
                            checked={formData.preferredContact === option.value}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                          />
                          <div className={`relative p-4 rounded-xl border-2 transition-colors ${
                            formData.preferredContact === option.value
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}>
                            <div className="flex items-center space-x-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                formData.preferredContact === option.value
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {option.icon}
                              </div>
                              <span className="font-medium text-gray-900">{option.label}</span>
                            </div>
                            {formData.preferredContact === option.value && (
                              <div className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="newsletter"
                      name="newsletter"
                      checked={formData.newsletter}
                      onChange={handleChange}
                      className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      disabled={isSubmitting}
                    />
                    <label htmlFor="newsletter" className="text-sm text-gray-600">
                      Subscribe to our newsletter for updates, offers, and agricultural insights.
                      <a href="/news" className="text-green-700 hover:underline ml-1">View past issues</a>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full sm:w-auto px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center space-x-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Sending...</span>
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Quick answers to common questions</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: 'What are your business hours?',
                a: 'Our showroom is open Monday-Friday 8AM-6PM and Saturday 9AM-4PM. Our service department offers 24/7 emergency support during harvest season.',
              },
              {
                q: 'Do you offer financing options?',
                a: 'Yes, we partner with multiple agricultural lenders to offer competitive financing with flexible terms including seasonal payment plans aligned with your cash flow.',
              },
              {
                q: 'Can I schedule a demo of equipment?',
                a: 'Absolutely! Contact our sales team to schedule a field demonstration of any equipment. We can bring the machine to your farm or you can visit our test plots.',
              },
              {
                q: 'Do you ship equipment nationwide?',
                a: 'Yes, we arrange delivery across the country through our logistics partners. Shipping costs vary by location and equipment size.',
              },
              {
                q: 'What brands do you carry?',
                a: 'We are authorized dealers for John Deere, Case IH, New Holland, Kubota, Massey Ferguson, Valley Irrigation, and many other leading brands.',
              },
              {
                q: 'Do you provide operator training?',
                a: 'Yes, we offer comprehensive operator training for all equipment purchases, including precision agriculture technology and safety protocols.',
              },
            ].map((faq, index) => (
              <details key={index} className="group card p-6">
                <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-gray-900">
                  {faq.q}
                  <svg className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-gray-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>      </section>

      {/* CTA */}
      <section className="section bg-green-700">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Prefer to Talk Directly?</h2>
          <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">
            Our knowledgeable team is ready to assist you with equipment selection, financing, service scheduling, and more.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:+15551234567" className="btn bg-white text-green-700 hover:bg-green-50 text-lg px-8 py-4">
              Call Sales: +1 (555) 123-4567
            </a>
            <a href="mailto:sales@siaedts.com" className="btn border-2 border-white text-white hover:bg-green-600 text-lg px-8 py-4">
              Email Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
}